import React, { FormEvent, useMemo, useRef, useState } from 'react';
import { BuildingIcon, CheckCircle2Icon, ClockIcon, FileSignatureIcon, FileTextIcon, LayoutGridIcon, ListIcon, MailIcon, PhoneIcon, PlusIcon, SearchIcon, SendHorizontalIcon, UserRoundIcon, XIcon } from 'lucide-react';
type CompanyType = 'Brand' | '3PL partner' | 'Strategic partner' | 'Portfolio company';
type Stage = 'Lead' | 'Qualified' | 'Contract sent' | 'Signed' | 'Activated';
type ContractStatus = 'None' | 'Sent' | 'Signed';
type Company = {
  id: string;
  name: string;
  domain: string;
  type: CompanyType;
  stage: Stage;
  owner: string;
  contract: ContractStatus;
  value: string;
  lastActivity: string;
  location: string;
};
type Activity = {
  id: string;
  company: string;
  kind: 'Email' | 'Contract' | 'Call' | 'Note';
  summary: string;
  owner: string;
  timestamp: string;
};
type ChatEntry = {
  id: number;
  author: 'carli' | 'user';
  text: string;
  citation?: string;
};
const stages: Stage[] = ['Lead', 'Qualified', 'Contract sent', 'Signed', 'Activated'];
const companyTypes: CompanyType[] = ['Brand', '3PL partner', 'Strategic partner', 'Portfolio company'];
const stageTone: Record<Stage, string> = {
  Lead: 'bg-ink/10 text-ink/60',
  Qualified: 'bg-[#4D9CC7]/15 text-[#2F6E8F]',
  'Contract sent': 'bg-[#C9A04E]/18 text-[#8B6A20]',
  Signed: 'bg-[#BB5E40]/12 text-[#94452E]',
  Activated: 'bg-forest/12 text-forest'
};
const typeTone: Record<CompanyType, string> = {
  Brand: 'bg-[#BB5E40]/10 text-[#94452E]',
  '3PL partner': 'bg-[#4D9CC7]/12 text-[#2F6E8F]',
  'Strategic partner': 'bg-[#A77ACA]/14 text-[#6B4A8C]',
  'Portfolio company': 'bg-forest/10 text-forest'
};
const initialCompanies: Company[] = [{
  id: 'eddy',
  name: 'Eddy',
  domain: 'eddy.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$210,600',
  lastActivity: '12m',
  location: 'New York, US'
}, {
  id: 'one-people',
  name: '1 People',
  domain: '1people.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$96,400',
  lastActivity: '1h',
  location: 'Copenhagen, DK'
}, {
  id: 'nature-baby',
  name: 'Nature Baby',
  domain: 'naturebaby.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$68,200',
  lastActivity: '3h',
  location: 'Auckland, NZ'
}, {
  id: 'matdalee',
  name: 'MATDALEE Studio',
  domain: 'matdalee.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$41,800',
  lastActivity: 'Yesterday',
  location: 'Los Angeles, US'
}, {
  id: 'seaesta',
  name: 'SEAESTA SURF',
  domain: 'seaestasurf.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meg Sawtelle',
  contract: 'Sent',
  value: '$52,000',
  lastActivity: '2d',
  location: 'San Diego, US'
}, {
  id: 'lili-blanc',
  name: 'Lili Blanc',
  domain: 'liliblanc.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meris Butler',
  contract: 'Sent',
  value: '$34,500',
  lastActivity: '2d',
  location: 'Dubai, AE'
}, {
  id: 'hermoza',
  name: 'Hermoza',
  domain: 'hermoza.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$28,900',
  lastActivity: '4d',
  location: 'Washington, US'
}, {
  id: 'no-more-nobody',
  name: 'No More Nobody',
  domain: 'nomorenobody.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$22,400',
  lastActivity: '5d',
  location: 'London, UK'
}, {
  id: 'arkitaip',
  name: 'arkitaip',
  domain: 'arkitaip.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$18,000',
  lastActivity: '1w',
  location: 'London, UK'
}, {
  id: 'il-gufo',
  name: 'Il Gufo',
  domain: 'ilgufo.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$46,000',
  lastActivity: '1w',
  location: 'Asolo, IT'
}, {
  id: 'shipbob',
  name: 'ShipBob',
  domain: 'shipbob.com',
  type: '3PL partner',
  stage: 'Activated',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '—',
  lastActivity: '3d',
  location: 'Chicago, US'
}, {
  id: 'flowspace',
  name: 'Flowspace',
  domain: 'flow.space',
  type: '3PL partner',
  stage: 'Contract sent',
  owner: 'Rich Amsinger',
  contract: 'Sent',
  value: '—',
  lastActivity: '6d',
  location: 'Los Angeles, US'
}, {
  id: 'poshmark-partner',
  name: 'Poshmark',
  domain: 'poshmark.com',
  type: 'Strategic partner',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '—',
  lastActivity: '1d',
  location: 'Redwood City, US'
}, {
  id: 'whatnot-partner',
  name: 'Whatnot',
  domain: 'whatnot.com',
  type: 'Strategic partner',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '—',
  lastActivity: '1w',
  location: 'Marina del Rey, US'
}, {
  id: 'bloomy',
  name: 'Bloomy Collective',
  domain: 'bloomycollective.com',
  type: 'Portfolio company',
  stage: 'Signed',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '$12,600',
  lastActivity: '2w',
  location: 'Austin, US'
}];
const initialActivity: Activity[] = [{
  id: 'a1',
  company: 'Eddy',
  kind: 'Email',
  summary: 'Sent Spring Collection recovery report and nine-month plan.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 9:42 AM'
}, {
  id: 'a2',
  company: 'SEAESTA SURF',
  kind: 'Contract',
  summary: 'Recovery agreement sent for signature.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 8:15 AM'
}, {
  id: 'a3',
  company: 'Nature Baby',
  kind: 'Note',
  summary: 'Unit counts on summer submission need confirmation.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 6:58 AM'
}, {
  id: 'a4',
  company: 'Lili Blanc',
  kind: 'Email',
  summary: 'Follow-up on excess inventory volume for Q3.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 4:20 PM'
}, {
  id: 'a5',
  company: 'MATDALEE Studio',
  kind: 'Contract',
  summary: 'Agreement countersigned — ready for onboarding.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 11:02 AM'
}, {
  id: 'a6',
  company: 'Flowspace',
  kind: 'Call',
  summary: 'Discussed intake SLAs and returns processing fees.',
  owner: 'Rich Amsinger',
  timestamp: 'Monday, 2:30 PM'
}];
const suggestedPrompts = ['Which brands have signed but not activated?', 'Email Lili Blanc about more excess inventory', 'Add a new company', 'What contracts are waiting on signature?'];
export function PipelineDashboard() {
  const [companies, setCompanies] = useState(initialCompanies);
  const [activity, setActivity] = useState(initialActivity);
  const [view, setView] = useState<'board' | 'companies' | 'activity'>('board');
  const [typeFilter, setTypeFilter] = useState<CompanyType | 'All'>('All');
  const [query, setQuery] = useState('');
  const [prompt, setPrompt] = useState('');
  const [chat, setChat] = useState<ChatEntry[]>([{
    id: 1,
    author: 'carli',
    text: 'Good morning, Rich. There are 4 companies signed but not yet activated, 3 contracts waiting on signature, and 2 leads with no activity in over a week. What would you like to work on?',
    citation: 'Pipeline · Contracts · Activity'
  }]);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    domain: '',
    type: 'Brand' as CompanyType,
    owner: 'Meris Butler',
    location: '',
    value: ''
  });
  const chatIdRef = useRef(2);
  const filteredCompanies = useMemo(() => companies.filter(company => {
    const matchesType = typeFilter === 'All' || company.type === typeFilter;
    const matchesQuery = company.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
    return matchesType && matchesQuery;
  }), [companies, typeFilter, query]);
  const signedNotActivated = companies.filter(company => company.stage === 'Signed');
  const contractsOut = companies.filter(company => company.contract === 'Sent');
  const respond = (text: string) => {
    const lower = text.toLocaleLowerCase();
    const nextId = () => chatIdRef.current++;
    if (lower.includes('add') && lower.includes('compan') || lower.includes('create') && lower.includes('compan')) {
      setCreateOpen(true);
      return {
        id: nextId(),
        author: 'carli' as const,
        text: 'I opened a new company record. Give me the domain, type, and owner and I will file it in the right pipeline stage.',
        citation: 'Pipeline · Companies'
      };
    }
    if (lower.includes('signed') && (lower.includes('not activated') || lower.includes("hasn't activated") || lower.includes('no activation'))) {
      setView('companies');
      setTypeFilter('All');
      return {
        id: nextId(),
        author: 'carli' as const,
        text: `${signedNotActivated.length} companies are signed but not activated: ${signedNotActivated.map(company => company.name).join(', ')}. None have submitted inventory yet — I can draft activation emails for all of them.`,
        citation: 'Pipeline · Contract status'
      };
    }
    if (lower.includes('contract')) {
      setView('activity');
      return {
        id: nextId(),
        author: 'carli' as const,
        text: `${contractsOut.length} contracts are out for signature: ${contractsOut.map(company => company.name).join(', ')}. The oldest has been pending 6 days. Want me to send a reminder?`,
        citation: 'Contracts · Last activity'
      };
    }
    if (lower.includes('email')) {
      const matched = companies.find(company => lower.includes(company.name.toLocaleLowerCase()));
      const target = matched?.name ?? 'the account';
      setActivity(current => [{
        id: `draft-${Date.now()}`,
        company: target,
        kind: 'Email',
        summary: 'Carli drafted an outreach email about additional excess inventory.',
        owner: 'Carli · pending review',
        timestamp: 'Just now'
      }, ...current]);
      return {
        id: nextId(),
        author: 'carli' as const,
        text: `I drafted an email to ${target} asking whether they have additional excess inventory this quarter, and referenced their last recovery results. It is logged in Activity and waiting for your approval before sending.`,
        citation: 'Activity · Email draft'
      };
    }
    return {
      id: nextId(),
      author: 'carli' as const,
      text: 'I can source leads, categorize companies, track contracts, draft outreach, and create new records. Try asking about signed accounts, pending contracts, or tell me the details of a company to add.',
      citation: 'Pipeline assistant'
    };
  };
  const submitPrompt = (event: FormEvent) => {
    event.preventDefault();
    const text = prompt.trim();
    if (!text) return;
    const reply = respond(text);
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'user',
      text
    }, reply]);
    setPrompt('');
  };
  const createCompany = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    setCompanies(current => [{
      id: draft.name.toLocaleLowerCase().replace(/\s+/g, '-'),
      name: draft.name.trim(),
      domain: draft.domain.trim() || `${draft.name.trim().toLocaleLowerCase().replace(/\s+/g, '')}.com`,
      type: draft.type,
      stage: 'Lead',
      owner: draft.owner,
      contract: 'None',
      value: draft.value.trim() || '—',
      lastActivity: 'Just now',
      location: draft.location.trim() || '—'
    }, ...current]);
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'carli',
      text: `${draft.name.trim()} is now in the pipeline as a ${draft.type.toLocaleLowerCase()} at the Lead stage, assigned to ${draft.owner}.`,
      citation: 'Pipeline · Companies'
    }]);
    setDraft({
      name: '',
      domain: '',
      type: 'Brand',
      owner: 'Meris Butler',
      location: '',
      value: ''
    });
    setCreateOpen(false);
    setView('companies');
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="pipeline-heading" data-id="element-1193" data-mp="3ab0qwi6o0unz">
      <section className="rounded-3xl border border-[#BB5E40]/20 bg-[#BB5E40]/5 p-5 sm:p-6" aria-labelledby="pipeline-heading" data-id="element-1194" data-mp="35ch6wnbmtsy9">
        <div className="flex items-center justify-between gap-4" data-id="element-1195" data-mp="1x1trd7yj38w">
          <div className="flex items-center gap-2" data-id="element-1196" data-mp="1vqvtu170ppp">
            <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
            WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain'
          }} data-id="element-1197" data-mp="3t2l70kpm3aki" />
            <h1 id="pipeline-heading" className="text-sm font-semibold" data-id="element-1198" data-mp="258gah29sjqah">Ask Carli</h1>
          </div>
          <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1199" data-mp="2a71e7bo94s3o">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1200" data-mp="1mhgrqisw3cpn" />
            Create company
          </button>
        </div>

        <ul className="mt-4 space-y-3" aria-live="polite" data-id="element-1201" data-mp="1qeuxu1l9x4df">
          {chat.slice(-4).map(entry => <li key={entry.id} className={entry.author === 'user' ? 'flex justify-end' : ''} data-id="element-1202" data-mp="1hgjbibrfwsqs">
              <div className={`max-w-2xl rounded-2xl px-4 py-3 text-sm leading-relaxed ${entry.author === 'user' ? 'bg-ink text-cream' : 'bg-paper text-ink/75 shadow-sm'}`} data-id="element-1203" data-mp="1hidq78c4bomf">
                {entry.text}
                {entry.citation && <p className="mt-2 text-[11px] uppercase tracking-wider text-ink/40" data-id="element-1204" data-mp="17tgg9tv1glh2">{entry.citation}</p>}
              </div>
            </li>)}
        </ul>

        <form onSubmit={submitPrompt} className="mt-4" data-id="element-1205" data-mp="12w6uco6uyrxz">
          <label htmlFor="pipeline-prompt" className="sr-only" data-id="element-1206" data-mp="1gsk2v7tezj0b">Ask Carli about your pipeline</label>
          <div className="flex items-center gap-2 rounded-2xl border border-ink/10 bg-paper p-2 pl-4" data-id="element-1207" data-mp="1bth2w1sak2z7">
            <input id="pipeline-prompt" value={prompt} onChange={event => setPrompt(event.target.value)} placeholder="Ask Carli to pull a list, draft an email, or add a company…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-1208" data-mp="1qi3kyb9az8u4" />
            <button type="submit" disabled={!prompt.trim()} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#BB5E40] px-3.5 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" data-id="element-1209" data-mp="3p1i248yzb4zn">
              <SendHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1210" data-mp="7jp4z2xjorg6" />
              Send
            </button>
          </div>
        </form>

        <div className="mt-3 flex flex-wrap gap-2" data-id="element-1211" data-mp="364pb6jdaag8h">
          {suggestedPrompts.map(item => <button key={item} type="button" onClick={() => {
          const reply = respond(item);
          setChat(current => [...current, {
            id: chatIdRef.current++,
            author: 'user',
            text: item
          }, reply]);
        }} className="rounded-full border border-[#BB5E40]/25 bg-paper px-3 py-1.5 text-xs font-medium text-ink/70 transition-colors hover:border-[#BB5E40]/50 hover:text-[#94452E]" data-id="element-1212" data-mp="3b3ackm49v5yi">
              {item}
            </button>)}
        </div>
      </section>

      <section className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Pipeline summary" data-id="element-1213" data-mp="2fsc2op8sjchf">
        <SummaryCard icon={BuildingIcon} label="Companies" value={`${companies.length}`} detail="Across all categories" data-id="element-1214" data-mp="2b1dlzojw4cdn" />
        <SummaryCard icon={FileSignatureIcon} label="Contracts out" value={`${contractsOut.length}`} detail="Awaiting signature" data-id="element-1215" data-mp="263c3b4qdmf2p" />
        <SummaryCard icon={CheckCircle2Icon} label="Signed, not activated" value={`${signedNotActivated.length}`} detail="Ready for onboarding" data-id="element-1216" data-mp="2150r4nclzshb" />
        <SummaryCard icon={ClockIcon} label="Open pipeline" value="$372k" detail="Estimated recovery value" data-id="element-1217" data-mp="1s2hrsd817xzt" />
      </section>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1218" data-mp="1n4q2zs2oorzu">
        <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-1219" data-mp="3mfr6cjffq9ox">
          {([['board', 'Board', LayoutGridIcon], ['companies', 'Companies', ListIcon], ['activity', 'Activity', MailIcon]] as const).map(([id, label, Icon]) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === id ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1220" data-mp="4eu0dxhpfp8e">
              <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1221" data-mp="o68pua2163ep" />
              {label}
            </button>)}
        </div>

        {view !== 'activity' && <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-1222" data-mp="e79g0o5j41q4">
            <div className="relative" data-id="element-1223" data-mp="j5uf2k91ofcz">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1224" data-mp="j4rs2neuex2c" />
              <label htmlFor="pipeline-search" className="sr-only" data-id="element-1225" data-mp="2yzbkm0i8f26f">Search companies</label>
              <input id="pipeline-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search companies" className="w-full rounded-xl border border-ink/15 bg-paper py-2.5 pl-8 pr-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-[#BB5E40]/35 sm:w-56" data-id="element-1226" data-mp="33tyyl5fm0e6r" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1227" data-mp="2co1q0nc87kdy">
              {(['All', ...companyTypes] as const).map(item => <button key={item} type="button" onClick={() => setTypeFilter(item)} aria-pressed={typeFilter === item} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${typeFilter === item ? 'bg-ink text-cream' : 'border border-ink/15 bg-paper text-ink/60 hover:text-ink'}`} data-id="element-1228" data-mp="28o9ga7b3j870">
                  {item}
                </button>)}
            </div>
          </div>}
      </div>

      {view === 'board' && <section className="mt-5 -mx-2 overflow-x-auto px-2 pb-2" aria-label="Pipeline board" data-id="element-1229" data-mp="18eh63ca4t2ub">
          <div className="grid min-w-[900px] grid-cols-5 gap-3" data-id="element-1230" data-mp="1de3jpmtvgl3n">
            {stages.map(stage => {
          const stageCompanies = filteredCompanies.filter(company => company.stage === stage);
          return <div key={stage} className="rounded-2xl border border-ink/10 bg-cream/45 p-3" data-id="element-1231" data-mp="1naykkcdmaxl3">
                  <div className="flex items-center justify-between gap-2" data-id="element-1232" data-mp="1rcjrlh3h0hnd">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink/55" data-id="element-1233" data-mp="1w3tei9xmx6vk">{stage}</h3>
                    <span className="rounded-full bg-paper px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-1234" data-mp="213y02edegrsn">{stageCompanies.length}</span>
                  </div>
                  <ul className="mt-3 space-y-2" data-id="element-1235" data-mp="260ob2nackx77">
                    {stageCompanies.map(company => <li key={company.id} data-id="element-1236" data-mp="3vjmvdudsckgu">
                        <article className="rounded-xl border border-ink/10 bg-paper p-3" data-id="element-1237" data-mp="3vko4vuf5kywg">
                          <p className="text-sm font-semibold text-ink" data-id="element-1238" data-mp="3qjidbzx8bhoi">{company.name}</p>
                          <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1239" data-mp="3lknks69gmer7">{company.domain}</p>
                          <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-1240" data-mp="3gmvs3a0zqoly">{company.type}</span>
                          <div className="mt-3 flex items-center justify-between gap-2 border-t border-ink/10 pt-2 text-[11px] text-ink/55" data-id="element-1241" data-mp="i6t62wv7zs4z">
                            <span className="truncate" data-id="element-1242" data-mp="8ef72bvj0zmi">{company.owner}</span>
                            <span className="shrink-0 font-semibold text-ink" data-id="element-1243" data-mp="3fu2k098rts9">{company.value}</span>
                          </div>
                        </article>
                      </li>)}
                    {stageCompanies.length === 0 && <li className="rounded-xl border border-dashed border-ink/15 px-2 py-6 text-center text-[11px] text-ink/40" data-id="element-1244" data-mp="w3pxp0j5xdmx">No companies</li>}
                  </ul>
                </div>;
        })}
          </div>
        </section>}

      {view === 'companies' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Companies" data-id="element-1245" data-mp="2dpovmk48b7uo">
          <div className="-mx-2 overflow-x-auto px-2" data-id="element-1246" data-mp="28r5d6touge72">
            <table className="w-full min-w-[820px] text-left text-xs" data-id="element-1247" data-mp="35gcv7rq98568">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1248" data-mp="35ho3o4una13o">
                <tr data-id="element-1249" data-mp="31erm8hgucb1b">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1250" data-mp="31glzraps90vd">Company</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1251" data-mp="2wloso9c2d1l4">Category</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1252" data-mp="2rndg3qudwcz7">Stage</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1253" data-mp="3p4ca9erupyhg">Contract</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1254" data-mp="3kd40uiqiud4a">Owner</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1255" data-mp="3kb0z51g0avjq">Opportunity</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1256" data-mp="3rq37l8r1n1ua">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1257" data-mp="5h21t8w3t6yp">
                {filteredCompanies.map(company => <tr key={company.id} data-id="element-1258" data-mp="9j5d9xirwbjz">
                    <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-1259" data-mp="9k6z8x1buigh">
                      <span className="flex items-center gap-2.5" data-id="element-1260" data-mp="egxbtadxavx5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cream text-[10px] font-bold text-ink/60" data-id="element-1261" data-mp="jg9xvswyzyyo">{company.name.slice(0, 2).toLocaleUpperCase()}</span>
                        <span className="min-w-0" data-id="element-1262" data-mp="t74mh3xovana">
                          <span className="block truncate" data-id="element-1263" data-mp="t8pikxznj1n9">{company.name}</span>
                          <span className="block truncate text-[11px] font-normal text-ink/45" data-id="element-1264" data-mp="x9syfkhp8xoh">{company.location}</span>
                        </span>
                      </span>
                    </th>
                    <td className="py-3 pr-3" data-id="element-1265" data-mp="2yaz519z46t4p"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-1266" data-mp="2y8389oz06ahu">{company.type}</span></td>
                    <td className="py-3 pr-3" data-id="element-1267" data-mp="380ioohunir0m"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stageTone[company.stage]}`} data-id="element-1268" data-mp="381ttmkzu24tb">{company.stage}</span></td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-1269" data-mp="3hyey957xxdwm">
                      <span className="inline-flex items-center gap-1.5" data-id="element-1270" data-mp="3hwvlholhtkzl">
                        {company.contract === 'Signed' ? <CheckCircle2Icon className="h-3.5 w-3.5 text-forest" aria-hidden="true" data-id="element-1271" data-mp="3cz2fowo3qszu" /> : company.contract === 'Sent' ? <FileTextIcon className="h-3.5 w-3.5 text-[#C9A04E]" aria-hidden="true" data-id="element-1272" data-mp="3qx0olmjnlx7y" /> : <span className="h-1.5 w-1.5 rounded-full bg-ink/20" aria-hidden="true" data-id="element-1273" data-mp="3m0jri2uh88iq" />}
                        {company.contract}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-1274" data-mp="1rnan2pgsfd7c">{company.owner}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1275" data-mp="73oscavlvb3z">{company.value}</td>
                    <td className="py-3 text-right whitespace-nowrap text-ink/55" data-id="element-1276" data-mp="c202kmptbnmq">{company.lastActivity}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredCompanies.length === 0 && <p className="py-8 text-center text-sm text-ink/55" data-id="element-1277" data-mp="2b5cf79g43w0">No companies match this filter.</p>}
        </section>}

      {view === 'activity' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Activity log" data-id="element-1278" data-mp="3pizbey9w6kcs">
          <h2 className="text-base font-semibold" data-id="element-1279" data-mp="3bjrds9tdnkap">Recent activity</h2>
          <p className="mt-1 text-xs text-ink/55" data-id="element-1280" data-mp="3fktbunbk5qb3">Emails, contracts, calls, and notes across every account.</p>
          <ol className="mt-5 space-y-2" data-id="element-1281" data-mp="19s4smow62jm5">
            {activity.map(item => <li key={item.id} className="flex gap-3 rounded-xl border border-ink/10 bg-cream/40 p-4" data-id="element-1282" data-mp="1dsoozq8x1j78">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-paper" data-id="element-1283" data-mp="p0pqg6yg7kq3">
                  {item.kind === 'Email' ? <MailIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1284" data-mp="k0l96eppulyt" /> : item.kind === 'Contract' ? <FileSignatureIcon className="h-4 w-4 text-[#C9A04E]" aria-hidden="true" data-id="element-1285" data-mp="f1qikqlibt45" /> : item.kind === 'Call' ? <PhoneIcon className="h-4 w-4 text-[#4D9CC7]" aria-hidden="true" data-id="element-1286" data-mp="a48djbam1y1f" /> : <UserRoundIcon className="h-4 w-4 text-ink/45" aria-hidden="true" data-id="element-1287" data-mp="63y8q1yk1do5" />}
                </span>
                <div className="min-w-0" data-id="element-1288" data-mp="1bepaod6robf">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1289" data-mp="3sihe595jxiih">
                    <p className="text-sm font-semibold text-ink" data-id="element-1290" data-mp="3sir3cuirvln4">{item.company}</p>
                    <span className="rounded-full bg-ink/7 px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-1291" data-mp="3nkppco07ecii">{item.kind}</span>
                    <span className="text-[11px] text-ink/45" data-id="element-1292" data-mp="3imefwedtm40t">{item.timestamp}</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-1293" data-mp="3el2llvk04uma">{item.summary}</p>
                  <p className="mt-1 text-[11px] text-ink/45" data-id="element-1294" data-mp="1u30extqpw7v9">{item.owner}</p>
                </div>
              </li>)}
          </ol>
        </section>}

      {createOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="create-company-heading" data-id="element-1295" data-mp="22z9c11r82ue8">
          <button type="button" className="absolute inset-0 cursor-default" onClick={() => setCreateOpen(false)} aria-label="Close create company" data-id="element-1296" data-mp="1aehx5x81jwql" />
          <form onSubmit={createCompany} className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-paper p-6 shadow-2xl" data-id="element-1297" data-mp="1kack34jasdz6">
            <div className="flex items-start justify-between gap-4" data-id="element-1298" data-mp="1p35o1q6xzia7">
              <div data-id="element-1299" data-mp="rmyu5qe0oiok">
                <h2 id="create-company-heading" className="text-lg font-semibold" data-id="element-1300" data-mp="rldyfxfyutp4">Create company</h2>
                <p className="mt-1 text-xs text-ink/55" data-id="element-1301" data-mp="wjyzu06yfjf5">Carli files new records at the Lead stage.</p>
              </div>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close" data-id="element-1302" data-mp="oirfwk0rlv6l"><XIcon className="h-4 w-4" data-id="element-1303" data-mp="yax21d4c5idv" /></button>
            </div>

            <div className="mt-5 space-y-3" data-id="element-1304" data-mp="yc85tcxs6q4z">
              <Field label="Company name" value={draft.name} onChange={value => setDraft(current => ({
            ...current,
            name: value
          }))} required data-id="element-1305" data-mp="tcc1fcl68wow" />
              <Field label="Domain" value={draft.domain} onChange={value => setDraft(current => ({
            ...current,
            domain: value
          }))} placeholder="brand.com" data-id="element-1306" data-mp="5njeu6qfw16y" />
              <div data-id="element-1307" data-mp="fkp5jdv55q6n">
                <label htmlFor="company-type" className="text-xs font-semibold text-ink/60" data-id="element-1308" data-mp="fl79yxryjapn">Category</label>
                <select id="company-type" value={draft.type} onChange={event => setDraft(current => ({
              ...current,
              type: event.target.value as CompanyType
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-1309" data-mp="amvzqlxr2y6w">
                  {companyTypes.map(item => <option key={item} value={item} data-id="element-1310" data-mp="2uwwms2mwz31f">{item}</option>)}
                </select>
              </div>
              <div data-id="element-1311" data-mp="2zvq0nw644w24">
                <label htmlFor="company-owner" className="text-xs font-semibold text-ink/60" data-id="element-1312" data-mp="2zv7xsgoy3jl8">Owner</label>
                <select id="company-owner" value={draft.owner} onChange={event => setDraft(current => ({
              ...current,
              owner: event.target.value
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-1313" data-mp="3nmn38ic0qh0p">
                  {['Meris Butler', 'Meg Sawtelle', 'Rich Amsinger'].map(item => <option key={item} value={item} data-id="element-1314" data-mp="acz7bc947cqd">{item}</option>)}
                </select>
              </div>
              <Field label="Location" value={draft.location} onChange={value => setDraft(current => ({
            ...current,
            location: value
          }))} placeholder="City, Country" data-id="element-1315" data-mp="f7ll1m2o5hdm" />
              <Field label="Estimated opportunity" value={draft.value} onChange={value => setDraft(current => ({
            ...current,
            value
          }))} placeholder="$25,000" data-id="element-1316" data-mp="26gm3kcwydfjx" />
            </div>

            <div className="mt-6 flex gap-2" data-id="element-1317" data-mp="1wlbyfgb9cs1x">
              <button type="submit" className="flex-1 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1318" data-mp="1wi6bo7q3q29d">Create company</button>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink/65 hover:bg-cream" data-id="element-1319" data-mp="2p6lkgcin3oc9">Cancel</button>
            </div>
          </form>
        </div>}
    </div>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  required
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const id = label.toLocaleLowerCase().replace(/\s+/g, '-');
  return <div data-id="element-1320" data-mp="3khrur1t24unn">
      <label htmlFor={id} className="text-xs font-semibold text-ink/60" data-id="element-1321" data-mp="3khiew71q83vo">{label}</label>
      <input id={id} value={value} required={required} placeholder={placeholder} onChange={event => onChange(event.target.value)} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink/35 focus:border-[#BB5E40]/35" data-id="element-1322" data-mp="3pgv1cqoor8xq" />
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof BuildingIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1323" data-mp="h0t7yjy8es5h">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1324" data-mp="gyyu1pldo0aw" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1325" data-mp="2dumupy0ds34b">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1326" data-mp="2is68kwt62dvq">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1327" data-mp="24ehsa3a12h3x">{detail}</p>
    </article>;
}